import React, { useMemo, useRef, useState } from 'react';
import { Form } from '@unform/web';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Button,
  Label,
  NavLink,
  CustomInput,
} from 'reactstrap';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { validateCPF, validateCNPJ } from 'validations-br';

import Input from '~/components/Inputs/Input';
import PageHeader from '~/components/Common/PageHeader';
import { defineAlignment } from '~/util/newLayoutHelpers';
import { LG } from '~/util/breakpointWidths';
import InputMask from '~/components/Inputs/InputMask';
import InputMaskDynamic from '~/components/Inputs/InputMaskDynamic';
import InputCurrency from '~/components/Inputs/InputCurrency';
import InputSelect from '~/components/Inputs/InputSelect';
import history from '~/services/history';
import api from '~/services/api';
import { getRole } from '~/util/getRole';
import { useStores } from '~/hooks/useStores';
import IndividualFields from './IndividualFields';
import CompanyFields from './CompanyFields';
import AddressFields from './AddressFields';
import PartnerFields from './PartnerFields';
import SplitRulesSection from './SplitRulesSection';

const SplitReceiver = () => {
  const formRef = useRef();

  const windowSize = useSelector(({ layout }) => layout.windowSize);
  const roles = useSelector(({ user }) => user.profile.roles);

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'salesman',
        'store-manager',
      ]),
    [roles]
  );

  const userId = useSelector(({ user }) => user.profile.id);
  const { stores, loadingStores } = useStores(role, userId);
  const [selectedStore, setSelectedStore] = useState(null);
  const [telephone, setTelephone] = useState('');

  const [serviceType, setServiceType] = useState('delivery');
  const [splitType, setSplitType] = useState('percentage');
  const [dynamicRules, setDynamicRules] = useState([]);
  const [transferInterval, setTransferInterval] = useState('Daily');
  const [transferEnabled, setTransferEnabled] = useState(true);
  const [accountType, setAccountType] = useState({
    label: 'Corrente',
    value: 'checking',
  });
  const [branchNumber, setBranchNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [isLiable, setIsLiable] = useState(false);
  const [transferDay, setTransferDay] = useState('');
  const [transferDayValidationError, setTransferDayValidationError] =
    useState('');

  const [personType, setPersonType] = useState('individual'); // 'individual' ou 'company'

  // ============================================
  // Lookup de recebedor por recipient_id (Pagar.me)
  // Regras:
  // - Sem loja selecionada: avisa e não trava fluxo
  // - Com loja selecionada: busca e preenche campos
  // - Se selecionar loja depois do ID preenchido: busca automaticamente
  // ============================================
  const [recipientIdValue, setRecipientIdValue] = useState('');
  const [isRecipientLookupLoading, setIsRecipientLookupLoading] =
    useState(false);
  const [importedAddress, setImportedAddress] = useState(null);

  const recipientLookupTimerRef = useRef(null);
  const lookupInFlightRef = useRef(false);
  const lastLookupKeyRef = useRef(null);
  const [isImportedRecipient, setIsImportedRecipient] = useState(false);
  const [importedRecipient, setImportedRecipient] = useState(null);

  const normalizeRecipientId = v => String(v || '').trim();
  const isValidRecipientId = v =>
    /^re_[a-z0-9]{8,}$/i.test(String(v || '').trim());

  const normalizeTransferInterval = interval => {
    const v = String(interval || '')
      .trim()
      .toLowerCase();
    if (v === 'daily') return 'Daily';
    if (v === 'weekly') return 'Weekly';
    if (v === 'monthly') return 'Monthly';

    if (v === 'Daily' || v === 'Weekly' || v === 'Monthly') return v;
    return 'Daily';
  };

  const clearRecipientIdError = () => {
    if (!formRef?.current?.setFieldError) return;
    formRef.current.setFieldError('recipient_id', null);
  };

  const setFormValueSafe = (field, value) => {
    if (!formRef?.current?.setFieldValue) return;
    formRef.current.setFieldValue(field, value ?? '');
  };

  const normalizePersonType = type => {
    const v = String(type || '').toLowerCase();
    if (v === 'corporation' || v === 'company') return 'company';
    if (v === 'individual') return 'individual';
    return null;
  };

  const formatPhoneFromApi = tel => {
    if (!tel) return '';
    const ddd = String(tel.ddd || '').trim();
    const number = String(tel.number || '').trim();
    if (!ddd && !number) return '';
    return ddd && number ? `(${ddd}) ${number}` : `${ddd}${number}`;
  };

  const buildBranchValue = bankAccount => {
    const main = String(bankAccount?.branch_number || '').trim();
    const digit = String(bankAccount?.branch_check_digit || '').trim();
    if (!main) return '';
    return digit ? `${main}-${digit}` : main;
  };

  const buildAccountValue = bankAccount => {
    const main = String(bankAccount?.account_number || '').trim();
    const digit = String(bankAccount?.account_check_digit || '').trim();
    if (!main) return '';
    return digit ? `${main}-${digit}` : main;
  };

  const mapAccountTypeOption = typeValue => {
    const v = String(typeValue || '').toLowerCase();
    if (v === 'savings') return { label: 'Poupança', value: 'savings' };
    return { label: 'Corrente', value: 'checking' };
  };

  const hasImportedValue = value => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return value > 0; // transfer_day da pagarme vem 0 quando não usa
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return Boolean(value);
  };

  const lockIfImportedAndHasValue = importedValue =>
    Boolean(isImportedRecipient) && hasImportedValue(importedValue);

  const fillFormFromRecipient = data => {
    if (!data) return;

    const normalizedType = normalizePersonType(data?.type);
    if (normalizedType) {
      setPersonType(normalizedType);
    }

    const partners = Array.isArray(data?.managing_partners)
      ? data.managing_partners
      : [];

    const representativePartner =
      partners.find(p => p?.self_declared_legal_representative === true) ||
      partners[0] ||
      null;

    if (normalizedType === 'company' && representativePartner) {
      formRef.current?.setData?.({
        partner: {
          partner_name: representativePartner?.name || '',
          partner_email: representativePartner?.email || '',
          partner_cpf: representativePartner?.document || '',
          partner_birth_date: representativePartner?.birthdate || '',
          partner_monthly_income: representativePartner?.monthly_income || '',
          partner_professional_occupation:
            representativePartner?.professional_occupation || '',
        },
      });

      setTimeout(() => {
        formRef.current?.setFieldValue?.(
          'partner.partner_name',
          representativePartner?.name || ''
        );
        formRef.current?.setFieldValue?.(
          'partner.partner_email',
          representativePartner?.email || ''
        );
        formRef.current?.setFieldValue?.(
          'partner.partner_cpf',
          representativePartner?.document || ''
        );
        formRef.current?.setFieldValue?.(
          'partner.partner_birth_date',
          representativePartner?.birthdate || ''
        );
        formRef.current?.setFieldValue?.(
          'partner.partner_monthly_income',
          representativePartner?.monthly_income || ''
        );
        formRef.current?.setFieldValue?.(
          'partner.partner_professional_occupation',
          representativePartner?.professional_occupation || ''
        );
      }, 0);
    }

    if (data?.address) {
      formRef.current?.setData?.({
        address: {
          cep: data.address.cep || '',
          street_name: data.address.street_name || '',
          number: data.address.number || '',
          neighborhood: data.address.neighborhood || '',
          complement: data.address.complement || 'N/A',
          state: data.address.state || '',
          city: data.address.city || '',
        },
      });

      setImportedAddress({
        cep: data.address.cep || '',
        street_name: data.address.street_name || '',
        number: data.address.number || '',
        neighborhood: data.address.neighborhood || '',
        complement: data.address.complement || 'N/A',
        state: data.address.state || '',
        city: data.address.city || '',
      });
    }

    setFormValueSafe('name', data?.name || '');
    setFormValueSafe('email', data?.email || '');
    setFormValueSafe('document', data?.document || '');

    const phone = formatPhoneFromApi(data?.telephone);
    setTelephone(phone);
    setFormValueSafe('telephone', phone);

    // extras (se existirem nos seus components)
    setFormValueSafe('individual_birth_date', data?.birthdate || '');
    setFormValueSafe('monthly_income', data?.monthly_income || '');
    setFormValueSafe(
      'professional_occupation',
      data?.professional_occupation || ''
    );

    setFormValueSafe('fantasy_name', data?.fantasy_name || '');
    setFormValueSafe('trading_name', data?.trading_name || '');
    setFormValueSafe('annual_revenue', data?.annual_revenue || '');

    // banco
    const bankAccount = data?.default_bank_account || {};
    setFormValueSafe('holder_name', bankAccount?.holder_name || '');
    setFormValueSafe('bank', bankAccount?.bank || '');

    const nextBranch = buildBranchValue(bankAccount);
    const nextAccount = buildAccountValue(bankAccount);

    setBranchNumber(nextBranch);
    setAccountNumber(nextAccount);

    setFormValueSafe('branch_number', nextBranch);
    setFormValueSafe('account_number', nextAccount);

    setAccountType(mapAccountTypeOption(bankAccount?.type));

    // transfer settings
    const transfer = data?.transfer_settings || {};
    const enabled = transfer?.transfer_enabled === true;
    setTransferEnabled(enabled);

    const normalizedInterval = normalizeTransferInterval(
      transfer?.transfer_interval
    );
    setTransferInterval(normalizedInterval);

    const day = transfer?.transfer_day;

    // Se transferência estiver desabilitada: não faz sentido exigir dia
    if (!enabled) {
      setTransferDay('');
      setTransferDayValidationError(null);
      setFormValueSafe('transfer_day', '');
      return;
    }

    // Se Daily: também não usa dia
    if (normalizedInterval === 'Daily') {
      setTransferDay('');
      setTransferDayValidationError(null);
      setFormValueSafe('transfer_day', '');
      return;
    }

    // Weekly/Monthly: só seta se vier um dia válido (>0)
    if (day !== null && day !== undefined && Number(day) > 0) {
      setTransferDay(String(day));
      setTransferDayValidationError(null);
      setFormValueSafe('transfer_day', String(day));
    } else {
      setTransferDay('');
      setTransferDayValidationError(null);
      setFormValueSafe('transfer_day', '');
    }
  };

  const fetchRecipientFromBackend = async (
    rawRecipientId,
    storeIdOverride = null,
    origin = 'manual'
  ) => {
    const recipientId = normalizeRecipientId(rawRecipientId);
    const storeId = storeIdOverride || selectedStore?.value || null;

    if (!recipientId) {
      lastLookupKeyRef.current = null;
      clearRecipientIdError();
      return;
    }

    if (!isValidRecipientId(recipientId)) {
      formRef.current?.setFieldError?.(
        'recipient_id',
        'ID do recebedor inválido. Use um ID no formato "re_...".'
      );
      return;
    }

    // Regra: sem loja -> apenas avisa (principalmente no blur)
    if (!storeId) {
      // evita spam: só avisa quando for blur/auto
      if (origin !== 'typing') {
        toast.info('Selecione uma loja para buscar os dados do recebedor.');
      }
      return;
    }

    const lookupKey = `${storeId}:${recipientId}`;

    // evita repetir
    if (lastLookupKeyRef.current === lookupKey) return;

    // evita corrida
    if (lookupInFlightRef.current) return;

    lookupInFlightRef.current = true;
    clearRecipientIdError();
    setIsRecipientLookupLoading(true);

    try {
      const { data } = await api.get(
        `/${role}/recipients/pagarme/${recipientId}`,
        {
          params: { store_id: storeId },
        }
      );

      lastLookupKeyRef.current = lookupKey;

      fillFormFromRecipient(data);
      setImportedRecipient(data);
      setIsImportedRecipient(true);
      toast.success('Dados do recebedor carregados com sucesso.');
    } catch (err) {
      lastLookupKeyRef.current = null;

      const status = err?.response?.status;

      if (status === 404) {
        toast.warn(err?.response?.data?.message || 'Recebedor não encontrado.');
        return;
      }

      if (status === 400) {
        toast.info(
          err?.response?.data?.message ||
            'Selecione uma loja para consultar o recebedor.'
        );
        return;
      }

      const message =
        err?.response?.data?.message ||
        'Não foi possível consultar a Pagar.me no momento.';
      toast.error(message);
    } finally {
      lookupInFlightRef.current = false;
      setIsRecipientLookupLoading(false);
    }
  };

  const handleRecipientIdChange = value => {
    const v = normalizeRecipientId(value);
    setRecipientIdValue(v);

    if (!v) {
      setIsImportedRecipient(false);
    }

    if (recipientLookupTimerRef.current) {
      clearTimeout(recipientLookupTimerRef.current);
      recipientLookupTimerRef.current = null;
    }

    clearRecipientIdError();

    // debounce: só busca se houver loja selecionada
    recipientLookupTimerRef.current = setTimeout(() => {
      if (isValidRecipientId(v) && selectedStore?.value) {
        fetchRecipientFromBackend(v, null, 'typing');
      }
    }, 600);
  };

  const handleRecipientIdBlur = value => {
    const v = normalizeRecipientId(value);
    setRecipientIdValue(v);

    if (!v) {
      setIsImportedRecipient(false);
    }

    if (recipientLookupTimerRef.current) {
      clearTimeout(recipientLookupTimerRef.current);
      recipientLookupTimerRef.current = null;
    }

    // blur: se não tiver loja, avisa. se tiver, busca.
    fetchRecipientFromBackend(v, null, 'blur');
  };

  const handleStoreChange = option => {
    setSelectedStore(option);

    formRef.current?.setFieldValue?.('store_id', option?.value || '');
    formRef.current?.setFieldError?.('store_id', null);

    // se já existe ID preenchido e válido, busca automaticamente
    const v = normalizeRecipientId(recipientIdValue);
    if (isValidRecipientId(v) && option?.value) {
      // limpa cache para garantir nova tentativa
      lastLookupKeyRef.current = null;
      fetchRecipientFromBackend(v, option.value, 'auto');
    }
  };

  const adjustMask = v => {
    const digits = v?.replace(/[^\d]/g, '') || '';
    if (digits.length < 10) return v;
    const cut = digits.length === 10 ? 6 : 7;
    return `(${digits.substring(0, 2)}) ${digits.substring(
      2,
      cut
    )}-${digits.substring(cut)}`;
  };

  const maskBuilder = v => {
    if (!v) return '';
    const mask = adjustMask(v);
    return mask.length >= 6 && mask[5] === '9'
      ? '(99) 99999-9999'
      : '(99) 9999-9999';
  };

  const documentSchema = Yup.string()
    .required('O CPF ou CNPJ é obrigatório')
    .test('cpf-cnpj', 'CPF ou CNPJ inválido', value => {
      if (!value) return false;

      const numericValue = value.replace(/\D/g, '');
      // console.log('Valor enviado para validação:', numericValue);

      if (numericValue.length <= 11) {
        // console.log('Validando CPF');
        return validateCPF(numericValue);
      }
      // console.log('Validando CNPJ');
      return validateCNPJ(numericValue);
    });

  function sanitizeAddress(address) {
    return {
      ...address,
      complement:
        address.complement && address.complement.trim() !== ''
          ? address.complement
          : 'N/A',
    };
  }

  const submitHandler = async formData => {
    formRef.current.setErrors({});

    const schema = Yup.object().shape({
      store_id: Yup.mixed().required('Selecione uma loja.'),
      name: Yup.string().required('O nome é obrigatório'),
      document: documentSchema,
      email: Yup.string().email('E-mail no formato inválido'),
      address: Yup.object().shape({
        cep: Yup.string().required('O CEP do endereço é obrigatório.'),
        street_name: Yup.string().required(
          'O campo rua do endereço é obrigatório.'
        ),
        number: Yup.string().required(
          'O campo número do endereço é obrigatório.'
        ),
        neighborhood: Yup.string().required(
          'O campo bairro do endereço é obrigatório.'
        ),
        city: Yup.string().required(
          'O campo cidade do endereço é obrigatório.'
        ),
        state: Yup.string().required(
          'O campo estado do endereço é obrigatório.'
        ),
      }),
      bank: Yup.string()
        .length(3, 'Código do Banco deve ter 3 caracteres')
        .required('O Código do Banco é obrigatório'),
      branch_number: Yup.string()
        .matches(/^[0-9]{1,4}(-[0-9]{1,2}){0,1}$/m, 'Formato inválido')
        .required('O Número da Agência é obrigatório'),
      account_number: Yup.string().required('O Número da Conta é obrigatório'),
      transfer_interval: transferEnabled
        ? Yup.string().required('O Intervalo de Transferência é obrigatório')
        : Yup.string().notRequired(),
      transfer_day:
        transferEnabled && transferInterval !== 'Daily'
          ? Yup.string()
              .required('O Dia da Transferência é obrigatório')
              .test(
                'transfer_day_validation',
                transferInterval === 'Weekly'
                  ? 'O Dia da Transferência Semanal deve ser um dia entre 1 e 5'
                  : 'O Dia da Transferência Mensal deve ser um dia entre 1 e 31',
                val => {
                  const n = Number(val);

                  if (transferInterval === 'Weekly') return n >= 1 && n <= 5;
                  if (transferInterval === 'Monthly') return n >= 1 && n <= 31;

                  return true;
                }
              )
          : Yup.string().notRequired(),
      // amount: Yup.string(),
      holder_name: Yup.string()
        .test(
          'len',
          'Nome/Razão Social não pode ultrapassar 30 caracteres',
          val => val.length <= 30
        )
        .required('O Nome/Razão Social é obrigatório'),
    });

    let transfer_day = 0;

    const [branchMain, branchCheckDigitRaw] = branchNumber.split('-');
    const [accountMain, accountCheckDigitRaw] = accountNumber.split('-');

    const branchCheckDigit = branchCheckDigitRaw || '';
    const accountCheckDigit = accountCheckDigitRaw || '';

    const resolvedStoreId = selectedStore?.value || formData?.store_id || null;

    try {
      if (transferEnabled && transferInterval !== 'Daily')
        transfer_day = transferDay;

      if (
        transferEnabled &&
        ['Weekly', 'Monthly'].includes(transferInterval) &&
        !transferDay
      ) {
        setTransferDayValidationError('O Dia da Transferência é obrigatório');
      } else {
        setTransferDayValidationError(null);
      }

      await schema.validate(
        {
          ...formData,
          store_id: resolvedStoreId,
          branch_number: branchMain,
          branch_check_digit: branchCheckDigit,
          transfer_interval: transferInterval,
          transfer_day,
          account_number: accountMain,
          account_check_digit: accountCheckDigit,
          // amount,
        },
        {
          abortEarly: false,
        }
      );
    } catch (err) {
      console.error(err);

      setTransferDayValidationError(null);

      const validationErrors = {};

      if (
        transferEnabled &&
        transferInterval === 'Weekly' &&
        (Number(transferDay) < 1 || Number(transferDay) > 5)
      ) {
        setTransferDayValidationError('O Dia da Transferência é obrigatório');
        validationErrors.transfer_day = 'O Dia da Transferência é obrigatório';
      }

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach(e => {
          if (e.path === 'account_check_digit')
            validationErrors.account_number = e.message;
          else validationErrors[e.path] = e.message;
        });
        formRef.current.setErrors(validationErrors);
      }
      return;
    }

    const transferSettingsPayload = { transfer_enabled: transferEnabled };

    if (transferEnabled) {
      transferSettingsPayload.transfer_interval = transferInterval;
      transferSettingsPayload.transfer_day =
        transferInterval === 'Daily' ? 0 : Number(transfer_day || 0);
    }

    const payload = {
      store_id: resolvedStoreId,
      recipient_id: formData.recipient_id || recipientIdValue || null,
      description: serviceType === 'delivery' ? 'Entrega' : 'Pagamento',
      name: formData.name,
      email: formData.email,
      telephone: formData.telephone,
      document: formData.document,
      type: personType, // Valores: "individual" (pessoa física) ou "company" (pessoa jurídica).
      service_type: serviceType, // required - Tipo de seviço. Valores: "delivery" ou "payment".
      birthdate: formData.individual_birth_date,
      monthly_income: formData.monthly_income,
      professional_occupation: formData.professional_occupation,
      fantasy_name: formData.fantasy_name,
      trading_name: formData.trading_name,
      annual_revenue: formData.annual_revenue,
      address: sanitizeAddress(formData.address),
      default_bank_account: {
        holder_name: formData.holder_name,
        holder_type: personType, // required - Tipo do documento. "individual" ou "company".
        holder_document: formData.document, // required - Número do doc. do titular. Deve ser igual ao documento do recebedor. Somente números
        bank: formData.bank, // required - Código do banco. Max 3.
        branch_number: branchMain, // required - Número da agência. Max 4.
        branch_check_digit: branchCheckDigit,
        account_number: accountMain, // required - Número da conta. Max: 13.
        account_check_digit: accountCheckDigit, // required - Digitos validadores da conta. Max 2.
        type: accountType.value, // required - Tipo da conta. Valores: "checking" ou "savings".
      },
      transfer_settings: transferSettingsPayload,
      split_rules:
        serviceType === 'payment'
          ? {
              amount: amount ? Number(amount) : null,
              type: splitType, // Tipo de divisão. Os valores possíveis são "flat" ou "percentage".
              liable: isLiable, // Indica se o recebedor vinculado à regra irá receber o restante dos recebíveis após uma divisão.
              charge_processing_fee: isLiable, // Indica se o recebedor é responsável pela transação em caso de chargeback.
              charge_remainder_fee: isLiable, // Indica se o recebedor vinculado à regra será cobrado pelas taxas da transação.
            }
          : null,
      dynamic_split_rules: dynamicRules,
    };

    if (personType === 'company') {
      payload.managing_partners = [
        {
          name: formData.partner?.partner_name,
          email: formData.partner?.partner_email,
          document: formData.partner?.partner_cpf,
          birthdate: formData.partner?.partner_birth_date,
          monthly_income: formData.partner?.partner_monthly_income,
          professional_occupation:
            formData.partner?.partner_professional_occupation,
          telephone: formData.telephone,
        },
      ];
    }

    try {
      await api.post(`/${role}/recipients`, payload);
      history.push('/recebedores-split');
    } catch (err) {
      const status = err?.response?.status;

      // ✅ Validação do backend (422)
      if (
        status === 422 &&
        err?.response?.data &&
        typeof err.response.data === 'object'
      ) {
        const apiErrors = err.response.data;

        const validationErrors = {};
        const keys = Object.keys(apiErrors);

        keys.forEach(key => {
          const msg = Array.isArray(apiErrors[key])
            ? apiErrors[key][0]
            : apiErrors[key];
          validationErrors[key] = msg;
        });

        formRef.current.setErrors(validationErrors);

        // toast amigável
        const hasAddressError = keys.some(k => k.startsWith('address.'));
        if (hasAddressError) {
          toast.warn('Preencha o endereço para salvar o recebedor.');
        } else {
          toast.warn(
            'Verifique os campos obrigatórios destacados em vermelho.'
          );
        }

        return;
      }

      // Mensagens padrão do backend
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      } else {
        toast.error(err.message);
      }
    }
  };

  const weekDays = useMemo(
    () => [
      {
        label: 'Segunda-feira',
        value: 1,
      },
      {
        label: 'Terça-feira',
        value: 2,
      },
      {
        label: 'Quarta-feira',
        value: 3,
      },
      {
        label: 'Quinta-feira',
        value: 4,
      },
      {
        label: 'Sexta-feira',
        value: 5,
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <Container className="container new-layout">
        <PageHeader
          pageTitle="Cadastro de Recebedor"
          backTo="/recebedores-split"
          responsivePosition="start"
          desktopPosition="center"
        />
        <Form ref={formRef} onSubmit={submitHandler} initialData={{}}>
          <Card className="card new-layout">
            <CardBody className="card-body new-layout">
              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Loja</Label>
                    <InputSelect
                      name="store_id"
                      options={stores}
                      onChange={handleStoreChange}
                      value={selectedStore}
                      isSearchable
                      isLoading={loadingStores}
                      loadingMessage={() => 'Buscando lojas...'}
                      placeholder="Selecione a loja"
                      singlePath
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2">
                  <FormGroup>
                    <Label>ID Recebedor</Label>
                    <Input
                      name="recipient_id"
                      type="text"
                      placeholder="ID do Recebedor"
                      onChange={e => handleRecipientIdChange(e?.target?.value)}
                      onBlur={e => handleRecipientIdBlur(e?.target?.value)}
                    />
                    {isRecipientLookupLoading && (
                      <small className="text-muted d-block mt-1">
                        Buscando dados do recebedor...
                      </small>
                    )}
                    {isImportedRecipient && (
                      <small className="text-muted d-block mt-1">
                        Dados importados da Pagar.me. Para alterar, edite no
                        recebedor da Pagar.me.
                      </small>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup className="mt-3">
                <Label>Tipo de Serviço</Label>
                <Row className="w-100 mx-0">
                  <Col lg={4} className="px-lg-0">
                    <NavLink
                      className={`
                        new-layout-nav-pill card d-flex flex-column
                        align-items-center h-fit-content w-100
                        ${serviceType === 'delivery' ? 'checked' : 'unchecked'}
                        font-size-14
                      `}
                      to="#"
                      onClick={() => setServiceType('delivery')}
                    >
                      <span className="text-nowrap">Entrega</span>
                    </NavLink>
                  </Col>
                  <Col lg={4}>
                    <NavLink
                      className={`
                        new-layout-nav-pill card d-flex flex-column
                        align-items-center h-fit-content w-100
                        ${serviceType === 'payment' ? 'checked' : 'unchecked'}
                        font-size-14
                      `}
                      to="#"
                      onClick={() => setServiceType('payment')}
                    >
                      <span className="text-nowrap">Pagamento</span>
                    </NavLink>
                  </Col>
                </Row>
              </FormGroup>

              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup className="mb-0">
                    <Row className="mx-0">
                      <Label>Nome</Label>
                      {serviceType === 'payment' && (
                        <CustomInput
                          type="checkbox"
                          id="liable"
                          name="liable"
                          label="Responsável por Chargeback e Taxas"
                          className="ml-auto text-primary z-index-0 mt-1"
                          checked={isLiable}
                          onChange={() => setIsLiable(state => !state)}
                        />
                      )}
                    </Row>
                    <Input
                      name="name"
                      disabled={lockIfImportedAndHasValue(
                        importedRecipient?.name
                      )}
                      type="text"
                      max="128"
                      placeholder="Nome do Recebedor"
                    />
                  </FormGroup>
                </Col>

                <Col lg={6} className="px-0 pl-lg-2">
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      name="email"
                      disabled={lockIfImportedAndHasValue(
                        importedRecipient?.email
                      )}
                      type="email"
                      placeholder="Email"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="w-100 mx-0">
                <Col lg={6} className="px-0 pr-lg-2">
                  <FormGroup>
                    <Label>Telefone</Label>
                    <InputMask
                      mask={maskBuilder(telephone)}
                      maskChar={null}
                      value={telephone}
                      name="telephone"
                      disabled={isImportedRecipient}
                      placeholder="Telefone"
                      type="text"
                      onChange={e => setTelephone(e.target.value)}
                    />
                  </FormGroup>
                </Col>

                {serviceType === 'payment' && (
                  <>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <FormGroup>
                        <Label className="d-flex align-items-center justify-content-between">
                          <span>Tipo de Divisão</span>
                          <div>
                            <CustomInput
                              type="radio"
                              id="percentage"
                              name="splitType"
                              label="Percentual"
                              inline
                              checked={splitType === 'percentage'}
                              onChange={() => setSplitType('percentage')}
                            />
                            <CustomInput
                              type="radio"
                              id="flat"
                              name="splitType"
                              label="Valor Fixo"
                              inline
                              checked={splitType === 'flat'}
                              onChange={() => setSplitType('flat')}
                            />
                          </div>
                        </Label>

                        {splitType === 'percentage' ? (
                          <Input
                            name="percentage"
                            type="number"
                            placeholder="Valor (%)"
                            value={amount}
                            onChange={e => {
                              if (Number(e.target.value) <= 100) {
                                formRef.current.setFieldError(
                                  'percentage',
                                  null
                                );
                                setAmount(e.target.value);
                                return;
                              }
                              formRef.current.setFieldError(
                                'percentage',
                                'Valor máximo é 100%'
                              );
                            }}
                            onFocus={() => {
                              formRef.current.setFieldError('percentage', null);
                            }}
                            onBlur={() => {
                              if (amount === '') {
                                formRef.current.setFieldError(
                                  'percentage',
                                  null
                                );
                                return;
                              }
                              if (Number(amount) > 100) {
                                formRef.current.setFieldError(
                                  'percentage',
                                  'O Percentual deve ser no máximo 100%'
                                );
                                return;
                              }
                              formRef.current.setFieldError('percentage', null);
                            }}
                          />
                        ) : (
                          <InputCurrency
                            name="fixed_amount"
                            placeholder="Valor Fixo (R$)"
                            type="text"
                            value={amount}
                            onValueChange={vals => setAmount(vals.floatValue)}
                            currency
                          />
                        )}
                      </FormGroup>
                    </Col>
                  </>
                )}
                {/*
                {serviceType === 'payment' && (
                  <Col lg={6} className="px-0 pl-lg-2">
                    <FormGroup>
                      <Label>Percentual (%)</Label>
                      <Input
                        name="percentage"
                        type="number"
                        placeholder="Valor"
                        value={amount}
                        onChange={e => {
                          const val = Number(e.target.value);
                          if (val <= 100) {
                            formRef.current.setFieldError('percentage', null);
                            setAmount(e.target.value);
                          } else {
                            formRef.current.setFieldError(
                              'percentage',
                              'Valor máximo é 100%'
                            );
                          }
                        }}
                        onFocus={() => {
                          formRef.current.setFieldError('percentage', null);
                        }}
                        onBlur={() => {
                          if (amount === '') {
                            formRef.current.setFieldError('percentage', null);
                            return;
                          }
                          if (Number(amount) > 100) {
                            formRef.current.setFieldError(
                              'percentage',
                              'O Percentual deve ser no máximo 100%'
                            );
                          } else {
                            formRef.current.setFieldError('percentage', null);
                          }
                        }}
                      />
                    </FormGroup>
                  </Col>
                )}
                */}
              </Row>

              {serviceType === 'payment' && (
                <SplitRulesSection
                  amount={amount}
                  setAmount={setAmount}
                  splitType={splitType}
                  setSplitType={setSplitType}
                  dynamicRules={dynamicRules}
                  setDynamicRules={setDynamicRules}
                />
              )}

              <FormGroup className="mt-3">
                <Label>Tipo de Pessoa</Label>
                <Row className="w-100 mx-0">
                  <Col lg={4} className="px-lg-0">
                    <NavLink
                      className={`
                            new-layout-nav-pill card d-flex flex-column
                            align-items-center h-fit-content w-100
                            ${
                              personType === 'individual'
                                ? 'checked'
                                : 'unchecked'
                            }
                            font-size-14
                          `}
                      to="#"
                      onClick={() => {
                        if (isImportedRecipient) return;
                        setPersonType('individual');
                      }}
                    >
                      <span className="text-nowrap">Pessoa Física</span>
                    </NavLink>
                  </Col>
                  <Col lg={4}>
                    <NavLink
                      className={`
                            new-layout-nav-pill card d-flex flex-column
                            align-items-center h-fit-content w-100
                            ${
                              personType === 'company' ? 'checked' : 'unchecked'
                            }
                            font-size-14
                          `}
                      to="#"
                      onClick={() => {
                        if (isImportedRecipient) return;
                        setPersonType('company');
                      }}
                    >
                      <span className="text-nowrap">Pessoa Jurídica</span>
                    </NavLink>
                  </Col>
                </Row>
                {isImportedRecipient && (
                  <small className="text-muted d-block mt-2">
                    Tipo de pessoa travado porque os dados foram importados da
                    Pagar.me.
                  </small>
                )}
              </FormGroup>
              <FormGroup>
                {personType === 'individual' && (
                  <IndividualFields
                    disabledFields={{
                      individual_birth_date:
                        isImportedRecipient &&
                        lockIfImportedAndHasValue(importedRecipient?.birthdate),
                      monthly_income:
                        isImportedRecipient &&
                        lockIfImportedAndHasValue(
                          importedRecipient?.monthly_income
                        ),
                      professional_occupation:
                        isImportedRecipient &&
                        lockIfImportedAndHasValue(
                          importedRecipient?.professional_occupation
                        ),
                    }}
                  />
                )}
                {personType === 'company' && (
                  <>
                    <CompanyFields
                      disabledFields={{
                        fantasy_name:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.fantasy_name
                          ),
                        trading_name:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.trading_name
                          ),
                        annual_revenue:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.annual_revenue
                          ),
                      }}
                    />
                    <PartnerFields
                      disabledFields={{
                        partner_name:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.managing_partners?.[0]?.name
                          ),
                        partner_email:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.managing_partners?.[0]?.email
                          ),
                        partner_cpf:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.managing_partners?.[0]?.document
                          ),
                        partner_birth_date:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.managing_partners?.[0]?.birthdate
                          ),
                        partner_monthly_income:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.managing_partners?.[0]
                              ?.monthly_income
                          ),
                        partner_professional_occupation:
                          isImportedRecipient &&
                          lockIfImportedAndHasValue(
                            importedRecipient?.managing_partners?.[0]
                              ?.professional_occupation
                          ),
                      }}
                    />
                  </>
                )}

                <AddressFields
                  formRef={formRef}
                  initialAddress={importedAddress}
                  disabledFields={{
                    cep:
                      isImportedRecipient &&
                      lockIfImportedAndHasValue(
                        importedRecipient?.address?.cep
                      ),
                    street_name:
                      isImportedRecipient &&
                      lockIfImportedAndHasValue(
                        importedRecipient?.address?.street_name
                      ),
                    number:
                      isImportedRecipient &&
                      lockIfImportedAndHasValue(
                        importedRecipient?.address?.number
                      ),
                    neighborhood:
                      isImportedRecipient &&
                      lockIfImportedAndHasValue(
                        importedRecipient?.address?.neighborhood
                      ),
                    complement:
                      isImportedRecipient &&
                      lockIfImportedAndHasValue(
                        importedRecipient?.address?.complement
                      ),
                    state:
                      isImportedRecipient &&
                      lockIfImportedAndHasValue(
                        importedRecipient?.address?.state
                      ),
                    city:
                      isImportedRecipient &&
                      lockIfImportedAndHasValue(
                        importedRecipient?.address?.city
                      ),
                  }}
                  disabled={isImportedRecipient}
                />
              </FormGroup>
              <FormGroup className="mt-3">
                <Label>Intervalo de Transferência</Label>
                <Row className="w-100 mx-0">
                  <Col lg={4} className="px-lg-0">
                    <NavLink
                      className={`
                        new-layout-nav-pill card d-flex flex-column
                        align-items-center h-fit-content w-100
                        ${
                          transferInterval === 'Daily' ? 'checked' : 'unchecked'
                        }
                        font-size-14
                      `}
                      to="#"
                      onClick={() => {
                        if (isImportedRecipient) return;
                        setTransferInterval('Daily');
                      }}
                    >
                      <span className="text-nowrap">Diário</span>
                    </NavLink>
                  </Col>
                  <Col lg={4}>
                    <NavLink
                      className={`
                        new-layout-nav-pill card d-flex flex-column
                        align-items-center h-fit-content w-100
                        ${
                          transferInterval === 'Weekly'
                            ? 'checked'
                            : 'unchecked'
                        }
                        font-size-14
                      `}
                      to="#"
                      onClick={() => {
                        if (isImportedRecipient) return;
                        setTransferInterval('Weekly');
                      }}
                    >
                      <span className="text-nowrap">Semanal</span>
                    </NavLink>
                  </Col>
                  <Col lg={4} className="px-lg-0">
                    <NavLink
                      className={`
                        new-layout-nav-pill card d-flex flex-column
                        align-items-center h-fit-content w-100
                        ${
                          transferInterval === 'Monthly'
                            ? 'checked'
                            : 'unchecked'
                        }
                        font-size-14
                      `}
                      to="#"
                      onClick={() => {
                        if (isImportedRecipient) return;
                        setTransferInterval('Monthly');
                      }}
                    >
                      <span className="text-nowrap">Mensal</span>
                    </NavLink>
                  </Col>
                </Row>
              </FormGroup>
              <Row className="w-100 mx-0 mt-3">
                <Col lg={6} className="pl-lg-0">
                  <FormGroup>
                    <Label>Dia da Transferência</Label>
                    {transferInterval === 'Daily' && (
                      <Input
                        name="transfer_day"
                        placeholder="Dia da Transferência"
                        disabled
                      />
                    )}
                    {transferInterval === 'Weekly' && (
                      <>
                        <Select
                          isDisabled={isImportedRecipient}
                          name="type"
                          label="Dia da Transferência"
                          placeholder="Selecione o Dia da Transferência"
                          options={weekDays}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              backgroundColor: state.isDisabled
                                ? '#ced4da9f !important'
                                : '#fff',
                              borderColor: transferDayValidationError
                                ? '#eb5757 !important'
                                : base.borderColor,
                              cursor: state.isDisabled
                                ? 'not-allowed'
                                : base.cursor,
                              opacity: state.isDisabled ? 0.65 : 1,
                              pointerEvents: state.isDisabled ? 'none' : 'auto',
                            }),
                            singleValue: base => ({
                              ...base,
                              color: '#6c757d',
                            }),
                            placeholder: base => ({
                              ...base,
                              color: '#6c757d',
                            }),
                          }}
                          theme={theme => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              neutral20: transferDayValidationError
                                ? '#eb5757'
                                : theme.colors.neutral20,
                            },
                          })}
                          classNamePrefix="select"
                          value={weekDays.find(
                            day => day.value === transferDay
                          )}
                          onChange={e => setTransferDay(e.value)}
                          onFocus={() => setTransferDayValidationError(null)}
                        />
                        {transferDayValidationError && (
                          <span
                            className="text-danger"
                            style={{
                              position: 'unset',
                              whiteSpace: 'unset',
                            }}
                          >
                            {transferDayValidationError}
                          </span>
                        )}
                      </>
                    )}
                    {transferInterval === 'Monthly' && (
                      <Input
                        name="transfer_day"
                        disabled={isImportedRecipient}
                        type="number"
                        placeholder="Dia da Transferência"
                        onChange={e => setTransferDay(e.target.value)}
                        value={transferDay}
                      />
                    )}
                  </FormGroup>
                </Col>
                <Col lg={6} className="px-0 pl-3">
                  <FormGroup>
                    <Label>Receber Pagamentos Automaticamente</Label>
                    <Row>
                      <Col xs={1}>
                        <CustomInput
                          type="radio"
                          disabled={isImportedRecipient}
                          id="mobile-select1"
                          name="transfer_enabled"
                          className="text-primary z-index-0"
                          checked={transferEnabled}
                          onChange={() => setTransferEnabled(true)}
                        />
                      </Col>
                      <Col xs={11}>
                        <label
                          className="new-layout-table-column-label text-dark mb-0"
                          htmlFor="mobile-select1"
                        >
                          Sim
                        </label>
                      </Col>

                      <Col xs={1}>
                        <CustomInput
                          type="radio"
                          id="mobile-select2"
                          name="transfer_enabled"
                          disabled={isImportedRecipient}
                          className="text-primary z-index-0"
                          checked={!transferEnabled}
                          onChange={() => setTransferEnabled(false)}
                        />
                      </Col>
                      <Col xs={11}>
                        <label
                          className="new-layout-table-column-label text-dark mb-0"
                          htmlFor="mobile-select2"
                        >
                          Não
                        </label>
                      </Col>
                    </Row>
                  </FormGroup>
                </Col>
              </Row>
              {isImportedRecipient && (
                <small className="text-muted d-block mt-2">
                  Intervalo de Transferência travado porque os dados foram
                  importados da Pagar.me.
                </small>
              )}
              <Row className="w-100 mx-0 mt-4">
                <Col className="d-flex flex-column align-items-start justify-content-center w-100 mx-0 px-0 font-size-18 font-weight-bold">
                  Dados Bancários
                </Col>
              </Row>
              <Row className="w-100 mx-0 mt-3">
                <Col lg={6} className="pl-lg-0">
                  <FormGroup>
                    <Label>Tipo de Conta</Label>
                    <Select
                      name="type"
                      isDisabled={isImportedRecipient}
                      label="Tipo de Conta"
                      placeholder="Selecione o Tipo de Conta"
                      options={[
                        {
                          label: 'Poupança',
                          value: 'savings',
                        },
                        {
                          label: 'Corrente',
                          value: 'checking',
                        },
                      ]}
                      classNamePrefix="select"
                      value={accountType}
                      onChange={setAccountType}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="pr-lg-0">
                  <FormGroup>
                    <Label>Código do Banco</Label>
                    <Input
                      name="bank"
                      disabled={lockIfImportedAndHasValue(
                        importedRecipient?.default_bank_account?.bank
                      )}
                      type="number"
                      placeholder="Código do Banco"
                      maxLength="3"
                    />
                  </FormGroup>
                </Col>

                <Col lg={6} className="pl-lg-0">
                  <FormGroup>
                    <Label>Agência</Label>
                    <InputMask
                      name="branch_number"
                      disabled={lockIfImportedAndHasValue(
                        importedRecipient?.default_bank_account?.branch_number
                      )}
                      placeholder="Agência (ex: 1234 ou 1234-5)"
                      value={branchNumber}
                      onChange={e => setBranchNumber(e.target.value)}
                      onBlur={e => {
                        const value = e.target.value.trim();
                        if (value && !/^\d{4}(-\d{1})?$/.test(value)) {
                          formRef.current.setFieldError(
                            'branch_number',
                            'Formato inválido. Ex: 1234 ou 1234-5'
                          );
                        }
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="pr-lg-0">
                  <FormGroup>
                    <Label>Número da Conta</Label>
                    <InputMask
                      name="account_number"
                      disabled={lockIfImportedAndHasValue(
                        importedRecipient?.default_bank_account?.account_number
                      )}
                      placeholder="Conta (ex: 12345-6)"
                      value={accountNumber}
                      onChange={e => setAccountNumber(e.target.value)}
                      onBlur={e => {
                        const value = e.target.value.trim();
                        if (value && !/^\d{5,12}-\d{1}$/.test(value)) {
                          formRef.current.setFieldError(
                            'account_number',
                            'Formato inválido. Use: 12345-6'
                          );
                        }
                      }}
                    />
                  </FormGroup>
                </Col>

                <Col lg={6} className="pl-lg-0">
                  <FormGroup>
                    <Label>CPF/CNPJ</Label>
                    <InputMaskDynamic
                      name="document"
                      disabled={lockIfImportedAndHasValue(
                        importedRecipient?.default_bank_account?.holder_document
                      )}
                      className="form-control"
                      placeholder="CPF ou CNPJ"
                      onBlur={async e => {
                        try {
                          await documentSchema.validate(e.target.value);
                        } catch (err) {
                          formRef.current.setFieldError(
                            'document',
                            err.message
                          );
                        }
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col lg={6} className="pr-lg-0">
                  <FormGroup>
                    <Label>Nome/Razão Social</Label>
                    <Input
                      name="holder_name"
                      disabled={lockIfImportedAndHasValue(
                        importedRecipient?.default_bank_account?.holder_name
                      )}
                      type="text"
                      placeholder="Nome ou Razão Social"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
          <Row className="w-100 mx-0 my-4">
            <Col
              align={defineAlignment(windowSize, LG, 'center', 'end')}
              className="px-0"
            >
              <Button
                color="primary"
                className="new-layout-btn main px-4 my-4"
                size="lg"
                type="submit"
              >
                Salvar
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default SplitReceiver;
